<script setup lang="ts">
import { computed, ref } from "vue";
const props = defineProps({
  parentPageTitle: { type: String, required: true },
});

const page = usePage().value;

function normalizeString(str: string) {
  return str.toLowerCase().trim().replace(/['"]/g, ""); // Remove apostrophes or quotes
}

const trekPackages = computed(() => {
  const normalizedParentPageTitle = normalizeString(props.parentPageTitle);
  const normalizedPageTitle = normalizeString(page?.title);

  return (
    page?.trekPackagesList
      ?.filter((trek) => {
        const trekRoute = normalizeString(trek?.trek_route);
        return trekRoute && normalizedParentPageTitle.includes(trekRoute);
      })
      .filter((trek) => {
        const trekTitle = normalizeString(trek?.trek_title);
        return trekTitle && normalizedPageTitle.includes(trekTitle);
      }) ?? []
  );
});

const trekPackagePrices1 = computed(() => {
  const trekRoutes = trekPackages.value.map((t) =>
    normalizeString(t.trek_route),
  );

  return (
    page?.trekprices1?.filter((trek) => {
      const route = normalizeString(trek?.trek_route);
      return route && trekRoutes.includes(route);
    }) ?? []
  );
});

const setYear1 = ref(true);

const currentTrek: Ref<null | (typeof trekPackages.value)[0]> = ref(null);
const currentTrekNum = ref(0);
const showPackageModal = ref(false);
const togglePackageModalYear1 = (trek) => {
  showPackageModal.value = !showPackageModal.value;
  currentTrek.value = trek;

  // Set currentTrekNum to the index of the selected trek in the filtered array
  const index = trekPackagePrices1.value.findIndex((t) => t.id === trek.id);

  currentTrekNum.value = index !== -1 ? index : 0; // If not found, fallback to 0
  console.log("currentTrekNum:", currentTrekNum.value); // Debugging: log currentTrekNum
};
const closePackageModal = () => {
  showPackageModal.value = false;
};

// const toggleForm = () => {
//   showForm.value = !showForm.value;
// };

// Check if this is a self-guided trek page
const isSelfGuided = computed(() => {
  return (
    page?.slug?.includes("self-guided") ||
    page?.title?.toLowerCase().includes("self-guided") ||
    trekPackages.value.some((trek) =>
      trek?.trek_title?.toLowerCase().includes("self-guided"),
    )
  );
});

const selfGuidedAvailabilityMessage = computed(() => {
  return page?.selfGuidedAvailabilityMessage || "Spaces Available";
});

// Check availability in trekPackagePrices1 for the corresponding trek
const isAvailable = (trekId: number) => {
  // Find the selected trek in trekPackages by id
  const trekPackage = trekPackages.value.find((trek) => trek.id === trekId);

  if (!trekPackage) {
    console.log(`Trek with ID ${trekId} not found in trekPackages`);
    return false;
  }

  // Match the trek in trekPackagePrices1 by trek_route
  const trek = trekPackagePrices1.value.find(
    (p) =>
      normalizeString(p.trek_route) === normalizeString(trekPackage.trek_route),
  );

  if (trek) {
    const availability = trek.availability;
    console.log("Matched Trek:", trek); // Log the trek object for verification
    console.log("Availability:", availability); // Log availability status
    return (
      availability !== "No Availability" && availability !== "Fully Booked"
    );
  } else {
    console.log(
      "🧩 Available routes in trekPackagePrices1:",
      trekPackagePrices1.value.map((p) => p.trek_route),
    );
    return false;
  }
};

console.log("🧩 Routes in trekPackagePrices1:");
trekPackagePrices1.value.forEach((p) => {
  console.log("🔍", p.trek_route, "→", normalizeString(p.trek_route));
});

// Expose the toggle function so parent components can trigger the modal
defineExpose({
  togglePackageModalYear1,
  openModalForFirstTrek: () => {
    // Use the same logic as the "View Dates" button - find first available trek
    // or use first trek if available (matching the button behavior)
    console.log(
      "openModalForFirstTrek called, trekPackages:",
      trekPackages.value,
    );

    // If trekPackages is empty, try to find the self-guided trek directly
    if (trekPackages.value.length === 0) {
      // For self-guided treks, find the trek directly from trekPackagesList
      const selfGuidedTrek = page?.trekPackagesList?.find(
        (trek) =>
          normalizeString(trek?.trek_route).includes("tour du mont blanc") &&
          normalizeString(trek?.trek_title).includes("full self-guided trek"),
      );

      if (selfGuidedTrek) {
        console.log("Found self-guided trek directly:", selfGuidedTrek);
        togglePackageModalYear1(selfGuidedTrek);
        return;
      }
    }

    if (trekPackages.value.length > 0) {
      // Try to find an available trek first
      const firstAvailableTrek = trekPackages.value.find((trek) =>
        isAvailable(trek.id),
      );
      if (firstAvailableTrek) {
        console.log("Opening modal with available trek:", firstAvailableTrek);
        togglePackageModalYear1(firstAvailableTrek);
      } else {
        // For self-guided treks that have dates (like Tour du Mont Blanc Full Self-Guided),
        // open with the first trek even if not marked as available
        console.log("Opening modal with first trek:", trekPackages.value[0]);
        togglePackageModalYear1(trekPackages.value[0]);
      }
    } else {
      console.log("No trek packages found");
    }
  },
});
</script>

<template>
  <Teleport to="body">
    <Transition name="pop" appear>
      <div v-if="showPackageModal" v-cloak class="modal trek-package-info">
        <button class="close-modal" @click="closePackageModal"></button>
        <AppSVGBrandGraphic />
        <div v-if="currentTrek" class="trek-details-wrapper">
          <div class="wrapper">
            <div class="trek-details">
              <div>
                <h4>{{ currentTrek?.trek_route }}</h4>
                <h3>{{ currentTrek?.trek_title }}</h3>
                <div v-html="currentTrek?.trek_details"></div>
              </div>
              <div class="trek-price">
                <section class="trek-price">
                  <template v-if="setYear1">
                    <div
                      v-for="trek in trekPackagePrices1.slice(
                        currentTrekNum,
                        currentTrekNum + 1,
                      )"
                      :key="trek"
                    >
                      <p class="price">&pound;{{ String(trek?.trek_price ?? "").trim() }}</p>
                      <p class="per">{{ trek?.trek_person_day }}</p>
                    </div>
                  </template>
                </section>
              </div>
            </div>
            <footer class="display-mobile">
              <NuxtLink
                class="btn enquire-btn"
                to="/get-in-touch"
                @click="closePackageModal"
              >
                Enquire now
              </NuxtLink>
            </footer>
            <div class="trek-info-wrapper">
              <div class="packages-dates-availability">
                <template v-if="setYear1">
                  <h4>Dates &amp; Availability for {{ page?.setYear1 }}</h4>

                  <div
                    v-for="trek in trekPackagePrices1.slice(
                      currentTrekNum,
                      currentTrekNum + 1,
                    )"
                    :key="trek"
                    class="dates-table"
                  >
                    <div
                      v-for="item in trek?.datesAvailability"
                      :key="item.id"
                      class="table-row"
                    >
                      <div class="dates">{{ item.dates }}</div>
                      <div
                        class="availability"
                        :class="
                          item?.availability
                            ? item.availability
                                .toLowerCase()
                                .replace(/\s+/g, '')
                            : ''
                        "
                      >
                        <span class="availability-label">
                          {{ item.availability }}
                        </span>
                        <span
                          v-if="item.spacesAvailable"
                          class="availability-detail"
                        >
                          — {{ item.spacesAvailable }} space<span
                            v-if="item.spacesAvailable !== '1'"
                            >s</span
                          >
                          available
                        </span>
                        <span
                          v-else-if="item.limitedAvailability"
                          class="availability-detail"
                        >
                          — {{ item.limitedAvailability }} space<span
                            v-if="item.limitedAvailability !== '1'"
                            >s</span
                          >
                          available
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="isSelfGuided && !trekPackagePrices1.length"
                    class="dates-table self-guided"
                  >
                    <div class="table-row">
                      <div class="availability self-guided">
                        {{ selfGuidedAvailabilityMessage }}
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <footer class="display-desktop">
          <NuxtLink
            class="btn enquire-btn"
            to="/get-in-touch"
            @click="closePackageModal"
          >
            Enquire now
          </NuxtLink>
        </footer>
      </div>
    </Transition>
    <Transition name="fade" appear>
      <div
        v-if="showPackageModal"
        v-cloak
        class="backdrop"
        @click="closePackageModal"
      ></div>
    </Transition>
  </Teleport>
  <span>
    <div
      v-for="trek in trekPackagePrices1.slice(0, 1)"
      :key="trek.id"
      :class="
        trek?.availability
          ? trek.availability.toLowerCase().replace(/\s+/g, '-')
          : ''
      "
    >
      {{ trek?.availability }}
    </div>
    <div
      v-if="isSelfGuided && !trekPackagePrices1.length"
      class="availability self-guided"
    >
      {{ selfGuidedAvailabilityMessage }}
    </div>
    <div v-for="trek in trekPackages" :key="trek.id">
      <button
        v-if="isAvailable(trek.id)"
        class="btn from-left"
        @click.prevent="togglePackageModalYear1(trek)"
      >
        View Dates
      </button>
      <NuxtLink
        v-if="isAvailable(trek.id) || isSelfGuided"
        class="btn from-left"
        to="/get-in-touch"
        @click="closePackageModal"
      >
        Enquire Now
      </NuxtLink>
    </div>
    <!-- Show Enquire Now for self-guided even if no treks found -->
    <div v-if="isSelfGuided && trekPackages.length === 0">
      <NuxtLink class="btn from-left" to="/get-in-touch">
        Enquire Now
      </NuxtLink>
    </div>
  </span>
</template>
